import { useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight, UserPlus, Search } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

export default function HeroSection() {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLElement>(null);
  const headlineRef = useRef<HTMLDivElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const leftCardRef = useRef<HTMLDivElement>(null);
  const rightCardRef = useRef<HTMLDivElement>(null);
  const trustBarRef = useRef<HTMLDivElement>(null);
  const watermarkRef = useRef<HTMLDivElement>(null);

  // Auto-play entrance animation on load
  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      // Watermark fade in
      tl.fromTo(
        watermarkRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.5 }
      );

      // Headline words reveal
      const headlineWords = headlineRef.current?.querySelectorAll('.word');
      if (headlineWords) {
        tl.fromTo(
          headlineWords,
          { y: 40, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.6, stagger: 0.05 },
          '-=0.3'
        );
      }

      // Subtitle fade + slide up
      tl.fromTo(
        subtitleRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5 },
        '-=0.3'
      );

      // Left card slide in
      tl.fromTo(
        leftCardRef.current,
        { x: '-12vw', opacity: 0 },
        { x: 0, opacity: 1, duration: 0.6 },
        '-=0.2'
      );

      // Right card slide in
      tl.fromTo(
        rightCardRef.current,
        { x: '12vw', opacity: 0 },
        { x: 0, opacity: 1, duration: 0.6 },
        '-=0.4'
      );

      // Trust bar fade in
      tl.fromTo(
        trustBarRef.current,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.4 },
        '-=0.2'
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // Scroll-driven exit animation
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const scrollTl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: '+=130%',
          pin: true,
          scrub: 0.6,
          onLeaveBack: () => {
            // Reset all elements to visible when scrolling back
            gsap.set([headlineRef.current, subtitleRef.current], {
              opacity: 1,
              y: 0,
            });
            gsap.set(leftCardRef.current, { opacity: 1, x: 0, y: 0 });
            gsap.set(rightCardRef.current, { opacity: 1, x: 0, y: 0 });
            gsap.set(trustBarRef.current, { opacity: 1, y: 0 });
          },
        },
      });

      // Phase 1 (0-30%): Hold - no entrance animation (already visible from load)
      // Phase 2 (30-70%): Settle - stable viewing
      // Phase 3 (70-100%): Exit

      // Headline exit
      scrollTl.fromTo(
        headlineRef.current,
        { y: 0, opacity: 1 },
        { y: '-18vh', opacity: 0, ease: 'power2.in' },
        0.7
      );

      // Subtitle exit
      scrollTl.fromTo(
        subtitleRef.current,
        { y: 0, opacity: 1 },
        { y: '-14vh', opacity: 0, ease: 'power2.in' },
        0.72
      );

      // Left card exit
      scrollTl.fromTo(
        leftCardRef.current,
        { x: 0, y: 0, opacity: 1 },
        { x: '-18vw', y: '10vh', opacity: 0, ease: 'power2.in' },
        0.7
      );

      // Right card exit
      scrollTl.fromTo(
        rightCardRef.current,
        { x: 0, y: 0, opacity: 1 },
        { x: '18vw', y: '10vh', opacity: 0, ease: 'power2.in' },
        0.72
      );

      // Trust bar exit
      scrollTl.fromTo(
        trustBarRef.current,
        { y: 0, opacity: 1 },
        { y: '10vh', opacity: 0, ease: 'power2.in' },
        0.75
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const headlineWords = 'CxO On Demand.'.split(' ');

  return (
    <section
      ref={sectionRef}
      className="section-pinned bg-navy flex items-center justify-center z-10"
    >
      {/* Circuit Watermark */}
      <div
        ref={watermarkRef}
        className="absolute inset-0 opacity-0 pointer-events-none"
      >
        <svg
          className="w-full h-full"
          viewBox="0 0 1920 1080"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 540H400M400 540V300M400 300H800M800 300V540M800 540H1200M1200 540V780M1200 780H1600M1600 780V540M1600 540H1920"
            stroke="rgba(255,255,255,0.04)"
            strokeWidth="1"
          />
          <path
            d="M200 0V200M200 200H600M600 200V400M600 400H1000M1000 400V200M1000 200H1400M1400 200V0"
            stroke="rgba(255,255,255,0.04)"
            strokeWidth="1"
          />
          <path
            d="M0 800H300M300 800V1000M300 1000H700M700 1000V800M700 800H1100M1100 800V1000M1100 1000H1500M1500 1000V800M1500 800H1920"
            stroke="rgba(255,255,255,0.04)"
            strokeWidth="1"
          />
          <circle cx="400" cy="300" r="4" fill="rgba(76,255,225,0.1)" />
          <circle cx="800" cy="300" r="4" fill="rgba(76,255,225,0.1)" />
          <circle cx="1200" cy="780" r="4" fill="rgba(76,255,225,0.1)" />
          <circle cx="1600" cy="780" r="4" fill="rgba(76,255,225,0.1)" />
        </svg>
      </div>

      {/* Content Container */}
      <div className="relative z-10 w-full px-6 lg:px-12 pt-20">
        {/* Headline Block */}
        <div className="text-center mb-6">
          <div ref={headlineRef} className="overflow-hidden">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-display font-bold text-white">
              {headlineWords.map((word, i) => (
                <span key={i} className="word inline-block mr-[0.25em]">
                  {word}
                </span>
              ))}
            </h1>
          </div>
          <p
            ref={subtitleRef}
            className="mt-6 text-lg lg:text-body-lg text-slate max-w-2xl mx-auto opacity-0"
          >
            Find verified fractional CIOs, CTOs, CFOs, CMOs, and COOs. Read their insights, then engage with confidence.
          </p>
        </div>

        {/* Dual CTA Cards */}
        <div className="flex flex-col lg:flex-row items-center justify-center gap-6 mt-12 max-w-4xl mx-auto">
          {/* Left Card - Companies */}
          <div
            ref={leftCardRef}
            className="surface-panel w-full lg:w-[28vw] p-6 lg:p-8 opacity-0"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-teal/10 flex items-center justify-center">
                <Search className="w-5 h-5 text-teal" />
              </div>
              <span className="font-mono text-xs uppercase tracking-widest text-slate">
                For Companies
              </span>
            </div>
            <h3 className="font-heading text-xl font-semibold text-white mb-2">
              I'm hiring leadership
            </h3>
            <p className="text-sm text-slate mb-6">
              Post your need and get matched with verified CXOs in 48 hours.
            </p>
            <button 
              onClick={() => navigate('/register')}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              Find a CXO
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Right Card - CXOs */}
          <div
            ref={rightCardRef}
            className="surface-panel w-full lg:w-[28vw] p-6 lg:p-8 opacity-0"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-teal/10 flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-teal" />
              </div>
              <span className="font-mono text-xs uppercase tracking-widest text-slate">
                For Executives
              </span>
            </div>
            <h3 className="font-heading text-xl font-semibold text-white mb-2">
              I'm a senior executive
            </h3>
            <p className="text-sm text-slate mb-6">
              List your services and get high-quality leads from APAC companies.
            </p>
            <button 
              onClick={() => navigate('/register')}
              className="btn-secondary w-full flex items-center justify-center gap-2"
            >
              Join as a CXO
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Trust Bar */}
        <div
          ref={trustBarRef}
          className="mt-12 text-center opacity-0"
        >
          <p className="text-sm text-slate">
            Trusted by 500+ APAC companies • PDPA-ready • E-sign + invoicing built-in
          </p>
        </div>
      </div>
    </section>
  );
}
